import React, { useCallback } from 'react';

// Components
import DataTableV2 from '@/views/components/data/DataTableV2/DataTableV2';
import EmptySearchResults from '@/views/components/data/DataTableV2/EmptySearchResults';
import { MenuItem } from '@/views/components/data/DataTableV2/TableMenu';

// Hooks
import { AccountColumns, useAccountColumnsDefinition } from './plan-accounts-table/useAccountColumnsDefinition';

// Types
import { Account } from '@/types/planning';

type Props = {
  columns: AccountColumns[];
  data: Account[];
  buttonActions?: {
    onCreatePortfolio?: (accountId: string) => void;
    onSelectPlanAccount?: (planAccountId: string) => void;
  };
  menuItems?: MenuItem<Account>[];
  isImportPage?: boolean;
  isSelectable: boolean;
  i18nBasePath: string;
  searchQuery?: string;
  selectedPlanAccountId?: string;
  onRowSelectionChange?: (selectedRows: Account[]) => void;
};

export const PlanAccountsTable = ({
  columns,
  data,
  menuItems,
  buttonActions,
  isImportPage,
  i18nBasePath,
  isSelectable,
  onRowSelectionChange,
  searchQuery,
  selectedPlanAccountId,
}: Props) => {
  const { getColumns, isRowDisabled, isRowForcefullySelected, getCheckboxTooltip } = useAccountColumnsDefinition({
    i18nBasePath,
    isImportPage,
    buttonActions,
    selectedPlanAccountId,
  });

  const getRowAriaLabelledBy = (row: { original: Account }) => `account-name-${row.original._id}`;

  const renderEmptyState = useCallback(
    () => (
      <EmptySearchResults
        messageToLocalize={`${i18nBasePath}.Table.NotMatch`}
        titleToLocalize={`${i18nBasePath}.Table.NoResultsFound`}
        searchQuery={searchQuery}
      />
    ),
    [i18nBasePath, searchQuery],
  );
  return (
    <DataTableV2<Account>
      columns={getColumns(columns)}
      data={data}
      noDataComponent={renderEmptyState}
      menuItems={menuItems}
      enableSorting
      enableTableSelection={isSelectable}
      enableMultiselect={isSelectable}
      onRowSelectionChange={onRowSelectionChange}
      rowSelectionDisabled={isRowDisabled}
      rowForcefullySelected={isRowForcefullySelected}
      getCheckboxTooltip={getCheckboxTooltip}
      getRowAriaLabelledBy={getRowAriaLabelledBy}
      tableClassNames={{
        isBodyHoverActive: true,
      }}
    />
  );
};
